import React, { useState, useEffect } from "react";
import { ArrowLeft, Download } from "lucide-react";
import { formatCurrency } from "../../tools/tool";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";

const ViewOrderInformation = ({ orderId, onClose, onProcessOrder }) => {
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [merchantWallet, setMerchantWallet] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [itemQuantities, setItemQuantities] = useState([]);
  const [savingQty, setSavingQty] = useState(false);

  // Decode JWT and extract name
  const getEmployeeName = () => {
    try {
      const token = localStorage.getItem('appToken');
      if (!token) return "Employee Name";

      // Decode JWT (split by . and decode base64 payload)
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);

      // Extract name from JWT claim
      const name = payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
      return name || "Employee Name";
    } catch (error) {
      console.error("Failed to decode JWT:", error);
      return "Employee Name";
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await adminServices.getOrderDetails(orderId);
      setOrderDetails(response.data);
      // Initialize local qty state from fetched items
      if (response.data?.orderItems) {
        setItemQuantities(response.data.orderItems.map(item => item.quantity));
      }
      // Fetch merchant wallet if merchant exists
      if (response.data?.order?.merchantId) {
        await fetchMerchantWallet(response.data.order.merchantId);
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantWallet = async (merchantId) => {
    try {
      const response = await adminServices.getMerchantWallet(merchantId);
      if (response.data && response.data.length > 0) {
        setMerchantWallet(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch merchant wallet:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const calculateTotal = () => {
    if (!orderDetails?.orderItems) return 0;
    return orderDetails.orderItems.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handleExportToPDF = () => {
    toast.info("Export to PDF functionality coming soon");
  };

  const handleQtyChange = (index, delta) => {
    setItemQuantities(prev => {
      const updated = [...prev];
      updated[index] = Math.max(1, (updated[index] || 1) + delta);
      return updated;
    });
  };

  const handleSaveQuantities = async () => {
    if (!orderDetails?.orderItems) return;
    try {
      setSavingQty(true);
      const items = orderDetails.orderItems.map((item, idx) => ({
        orderItemId: item.id ?? item.orderItemId,
        productId: item.productId,
        quantity: itemQuantities[idx] ?? item.quantity
      }));
      await adminServices.storeProcedure2('updateOrderItemQty', {
        orderId: Number(orderId),
        items
      });
      toast.success("Quantities updated successfully");
      await fetchOrderDetails();
    } catch (error) {
      console.error("Failed to update quantities:", error);
      toast.error(error.response?.data?.message || "Failed to update quantities");
    } finally {
      setSavingQty(false);
    }
  };

  const handleProcessOrder = async () => {
    if (!orderId) return;
    
    try {
      setProcessing(true);
      await adminServices.createQuotationRequest(orderId);
      toast.success("Quotation request created successfully");
      
      // Call parent callback to show process order view
      if (onProcessOrder) {
        onProcessOrder(orderId);
      }
    } catch (error) {
      console.error("Failed to create quotation request:", error);
      toast.error(error.response?.data?.message || "Failed to create quotation request");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="order-information-page">
        <div className="order-information-header">
          <button className="order-information-back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="order-information-title">Order Information</h1>
        </div>
        <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="order-information-page">
        <div className="order-information-header">
          <button className="order-information-back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="order-information-title">Order Information</h1>
        </div>
        <div style={{ padding: "40px", textAlign: "center" }}>Order not found</div>
      </div>
    );
  }

  const { order, orderItems } = orderDetails;

  return (
    <div className="order-information-page">
      <div className="order-information-header">
        <button className="order-information-back-btn" onClick={onClose}>
          <ArrowLeft size={20} />
        </button>
        <div className="order-information-header-content">
          <div className="order-information-title-section">
            <h1 className="order-information-title">{order.orderNo}</h1>
            <span className="view-order-status-badge">{order.status}</span>
          </div>
          <button className="export-pdf-btn" onClick={handleExportToPDF}>
            <Download size={16} />
            Export to PDF
          </button>
        </div>
      </div>

      <div className="order-information-content">
        {/* Order Information Box */}
        <div className="view-order-info-box">
          <h3 className="view-order-info-title">Order Information</h3>
          <div className="view-order-info-grid">
            <div className="view-order-info-field">
              <label>Order ID</label>
              <div className="view-order-info-value">{order.orderNo}</div>
            </div>
            <div className="view-order-info-field">
              <label>Issued Date</label>
              <div className="view-order-info-value">{formatDate(order.regDate)}</div>
            </div>
            <div className="view-order-info-field">
              <label>Order Type</label>
              <div className="view-order-info-value">{order.type || "Regular Order"}</div>
            </div>
            <div className="view-order-info-field">
              <label>Account Type</label>
              <div className="view-order-info-value">{order.accountType}</div>
            </div>
            <div className="view-order-info-field">
              <label>Name</label>
              <div className="view-order-info-value">
                {order.client ? `${order.client.firstName} ${order.client.lastName}` : "-"}
              </div>
            </div>
            <div className="view-order-info-field">
              <label>PDL Number</label>
              <div className="view-order-info-value">{order.client?.pdlNumber || "-"}</div>
            </div>
            <div className="view-order-info-field">
              <label>Location</label>
              <div className="view-order-info-value">{order.colony?.colonyName || "-"}</div>
            </div>
            <div className="view-order-info-field">
              <label>Store Code</label>
              <div className="view-order-info-value">{order.merchant?.id || "-"}</div>
            </div>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="view-order-wallet">
          <label className="view-order-wallet-label">
            Wallet Balance <span className="info-icon">ⓘ</span>
          </label>
          <div className="view-order-wallet-amount">
            {merchantWallet ? formatCurrency(merchantWallet.wallet_amount) : "Loading..."}
          </div>
        </div>

        {/* Ordered Items */}
        <div className="view-order-items-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 className="view-order-section-title" style={{ margin: 0 }}>Ordered Items</h3>
            {order.status === "PENDING" && (
              <button
                className="view-order-process-btn"
                style={{ padding: "6px 14px", fontSize: "0.8rem" }}
                onClick={handleSaveQuantities}
                disabled={savingQty}
              >
                {savingQty ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
          <div className="view-order-items-table">
            <table>
              <thead>
                <tr>
                  <th>SKU Code</th>
                  <th>Item Description</th>
                  <th>QTY</th>
                  <th>Min. Order QTY</th>
                </tr>
              </thead>
              <tbody>
                {orderItems && orderItems.length > 0 ? (
                  orderItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.sku}</td>
                      <td>{item.productName}</td>
                      <td>
                        <div className="view-order-qty-controls">
                          <button
                            className="view-order-qty-btn"
                            onClick={() => handleQtyChange(index, -1)}
                            disabled={order.status !== "PENDING"}
                          >−</button>
                          <span className="view-order-qty-value">{itemQuantities[index] ?? item.quantity}</span>
                          <button
                            className="view-order-qty-btn"
                            onClick={() => handleQtyChange(index, 1)}
                            disabled={order.status !== "PENDING"}
                          >+</button>
                        </div>
                      </td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                      No items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="view-order-total-section">
            <div className="view-order-total-row">
              <span className="view-order-total-label">Total</span>
              <span className="view-order-total-amount">{formatCurrency(calculateTotal())}</span>
            </div>
            <div className="view-order-total-note">
              {order.type === "SPECIAL" 
                ? "Please ensure to submit attahcments and awaits for the approvals."
                : "Final cost may change. Please ensure your wallet balance is higher than the Order total for successful transaction."}
            </div>
          </div>
        </div>

        {/* Processed By */}
        <div className="view-order-processed-by">
          <label>Processed by</label>
          <div className="view-order-processed-by-value">{getEmployeeName()}</div>
        </div>

        {/* Process Order Button - Only show if status is Pending */}
        {order.status === "PENDING" && (
          <div className="view-order-actions">
            <button 
              className="view-order-process-btn" 
              onClick={handleProcessOrder}
              disabled={processing}
            >
              {processing ? "Processing..." : "Process Order"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOrderInformation;
